import React from 'react'

// One side's picked players, as removable pills. Shared by the trade builder
// and the explorer, which differ in how players are chosen but not in how a
// pending selection reads back.
//
// `entries` are `{ player }` shaped so either page can carry whatever else it
// needs on the entry without this component caring.
//
// When `onRoute` and more than one `target` are given, each pill also carries
// the other end of the move. A two-team trade has only one possible answer, so
// the control is omitted and the pill reads exactly as it always did — the
// picker only appears once there is a choice to make.
//
// Which end that is depends on the caller: the builder groups by who gives a
// player up and picks the destination, the explorer groups by who receives one
// and picks the source. Hence `routeValue` and the arrow rather than a
// hardcoded "to".
export default function SelectionChips({
  label,
  entries,
  onRemove,
  onRoute = null,
  targets = [],
  routeValue = (entry) => entry.toTeamId,
  routeArrow = '→',
  routeHint = 'goes to',
  empty = 'Nobody yet',
}) {
  const routable = Boolean(onRoute) && targets.length > 1

  return (
    <div className="min-w-0 flex-1">
      <div className="mb-1 whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label} ({entries.length})
      </div>

      {entries.length === 0 ? (
        <p className="text-xs text-slate-500">{empty}</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {entries.map((entry) => {
            const { player } = entry

            return (
              <li key={player.id}>
                <span className="flex items-center gap-1.5 rounded-full py-1 pl-2.5 pr-1 text-xs border border-slate-600 bg-slate-900 text-slate-200">
                  <span className="font-medium">{player.name}</span>
                  <span className="text-slate-400">{player.position}</span>

                  {routable && (
                    <span className="flex items-center gap-1">
                      <span aria-hidden="true" className="text-slate-500">
                        {routeArrow}
                      </span>
                      <select
                        className="max-w-28 truncate rounded border px-1 py-0.5 text-xs border-slate-600 bg-slate-800 text-slate-200"
                        value={routeValue(entry) ?? ''}
                        onChange={(e) => onRoute(player, e.target.value)}
                        aria-label={`Which team ${player.name} ${routeHint}`}
                      >
                        {targets.map((target) => (
                          <option key={target.id} value={target.id}>
                            {target.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => onRemove(player)}
                    className="rounded-full px-1 text-slate-400 hover:text-rose-300"
                    title={`Remove ${player.name}`}
                  >
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">Remove {player.name}</span>
                  </button>
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
